import axios from 'axios';
import { BASE_URL } from '../actions';

// Keep consent requests out of Redux logging and legacy automatic retries.
const client = axios.create({ baseURL: `${BASE_URL}/legal`, timeout: 30000 });
const validDocument = (document, name) => document &&
  typeof document.version === 'string' && /^[A-Za-z0-9._-]+$/.test(document.version) &&
  document.url === `https://mycasecare.com/legal/${document.version}/${name}.html` &&
  typeof document.sha256 === 'string' && /^[a-f0-9]{64}$/.test(document.sha256);

async function request(method, url, data, token) {
  try {
    const response = await client.request({ method, url, data,
      ...(method === 'post' ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    });
    if (response.data?.ok !== true || !response.data.data) throw new Error('Invalid legal response');
    return response.data.data;
  } catch (error) {
    // Do not expose request headers or the Axios configuration to the UI.
    const safe = new Error('Unable to load or save your agreement. Please try again.');
    safe.status = error.response?.status;
    safe.code = error.response?.data?.code;
    throw safe;
  }
}

export const legalApi = {
  async policies() {
    const policies = await request('get', '/policies');
    if (!validDocument(policies.terms, 'terms') || !validDocument(policies.privacy, 'privacy')) {
      throw new Error('Unable to load the current policies. Please try again.');
    }
    return policies;
  },
  async accept(policies, token) {
    if (typeof token !== 'string' || !token) throw new Error('Please restart signup to save your agreement.');
    const receipt = await request('post', '/acceptances', {
      source: 'web',
      terms_version: policies.terms.version,
      privacy_version: policies.privacy.version,
      accept_terms: true,
      accept_privacy: true,
    }, token);
    if (!receipt.id || receipt.actor_type !== 'agent' || receipt.source !== 'web' ||
        !Number.isSafeInteger(receipt.actor_id) || receipt.actor_id <= 0 ||
        typeof receipt.accepted_at !== 'string' || !Number.isFinite(Date.parse(receipt.accepted_at)) ||
        ['terms', 'privacy'].some(name => !validDocument(receipt[name], name) ||
          receipt[name].version !== policies[name].version || receipt[name].sha256 !== policies[name].sha256)) {
      throw new Error('Unable to confirm that your agreement was saved. Please try again.');
    }
    return receipt;
  },
};
